'use client';

/* eslint-disable consistent-return */
import type { ReactNode } from 'react';
import React, { cloneElement, useContext, useMemo } from 'react';
import type { TargetHoverBindingProps } from 'components/Tooltip/useHoverTarget';
import { isFunction } from 'utils/is-function';
import { noop } from 'utils/noop';
import { mergeProps, mergeRefs } from '@react-aria/utils';
import { theme } from '@frameio/vapor-theme';
import type { PopoverContentProps } from './PopoverContent';
import { PopoverContent } from './PopoverContent';
import { PopoverContext } from './PopoverContext';
import type { TargetClickBindingProps } from './useClickTarget';
import type { UsePopoverProps } from './usePopover';
import { usePopover } from './usePopover';

const PERMITTED_TAB_INDEXES = [-1, 0];

export type SharedTargetAttributes = TargetHoverBindingProps &
  TargetClickBindingProps;

export type TargetAttributes = {
  isShowing: boolean;
};

export type PopoverTargetRenderCallback = (
  props: TargetAttributes
) => ReactNode;

export type PopoverTargetChildren = ReactNode | PopoverTargetRenderCallback;

export type ContentAttributes = {
  id: string;
  isShowing: boolean;
  targetEl: any;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  role: string;
  'aria-modal'?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  popoverRef: any;
};

export type PopoverContentRenderCallback = (
  props: ContentAttributes
) => ReactNode;

export type PopoverProps = {
  /** Provide an HTML element to position a popover. */
  anchorElement?: HTMLElement;
  /** The target element which triggers the popover to show */
  children?: PopoverTargetChildren;
  /** Optional className which styles the popover content container. */
  className?: string;
  /** Content to render in a popover */
  content: ReactNode | PopoverContentRenderCallback;
  /** Render a tooltip with the same trigger / target as a popover. */
  tooltip?: ReactNode;
} & Partial<UsePopoverProps> &
  Pick<
    PopoverContentProps,
    | 'hasOutermostTheme'
    | 'anchorPosition'
    | 'autoHide'
    | 'boundary'
    | 'color'
    | 'fallbackPlacements'
    | 'hideOverflow'
    | 'mountDuration'
    | 'observeContentSize'
    | 'observeTargetSize'
    | 'offset'
    | 'overflowPadding'
    | 'placement'
    | 'shadow'
    | 'shouldAnimate'
    | 'shouldUsePortal'
    | 'unmountDuration'
    | 'zIndex'
  >;

/**
 * Popover
 *
 * Handles displaying menus using absolute positioning,
 * triggered when the target is clicked.
 */

export function Popover({
  isShowing: isShowingProp,
  onRequestChange = noop,
  content,
  placement = 'bottom',
  fallbackPlacements,
  children,
  shouldUsePortal = true,
  shouldCloseOnEscape = true,
  shouldFocusOnMount = true,
  defaultShowing = false,
  hasRefocusEventOnClose,
  hideOverflow = false,
  initialFocusRef,
  className,
  anchorElement,
  anchorPosition,
  tooltip,
  zIndex = theme.zIndices.popover,
  color: colorProp,
  offset,
  boundary,
  autoHide,
  observeContentSize = false,
  observeTargetSize = false,
  shadow = '12dp',
  shouldAnimate = true,
  overflowPadding = 8,
  mountDuration = 200,
  unmountDuration = 125,
  hasOutermostTheme = false,
  ariaLabel,
}: PopoverProps) {
  const { color: parentColor } = useContext(PopoverContext);
  const color = colorProp ?? parentColor;

  const { bindTarget, bindContent, isShowing, hidePopover } = usePopover({
    isShowing: isShowingProp,
    hasRefocusEventOnClose,
    onRequestChange,
    defaultShowing,
    initialFocusRef,
    shouldFocusOnMount,
    shouldCloseOnEscape,
    targetEl: anchorElement,
    ariaLabel,
  });

  const popoverContext = useMemo(
    () => ({
      isWithinPopover: true,
      onRequestClose: hidePopover,
      color,
      zIndex,
    }),
    [color, hidePopover, zIndex]
  );

  const renderChildren = () => {
    if (!children) return null;

    const renderedChildren = isFunction(children)
      // @ts-expect-error FIXME(strictNullChecks)
      ? children({ isShowing })
      : children;

    const child = React.Children.only(renderedChildren) as any;

    const { ref, ...otherTargetBindings } = bindTarget as any;

    // React 19 warns when a ref is accessed directly from a child element.
    // This is a workaround to avoid the warning, while still supporting React 18
    // and below.
    const mergedRefs = mergeRefs(ref, child.props.ref ?? child.ref);

    const popoverChild = cloneElement(child, {
      ...(PERMITTED_TAB_INDEXES.includes(child.props.tabIndex) && {
        tabIndex: child.props.tabIndex,
      }),
      ...mergeProps(child.props, otherTargetBindings, {
        ref: mergedRefs,
      }),
    });

    if (!tooltip) {
      return popoverChild;
    }

    // If provided with a tooltip prop, we need to wrap our popover
    // child with the tooltip component too.
    const tooltipComponent = React.Children.only(tooltip);

    return cloneElement(tooltipComponent as any, {
      children: popoverChild,
    });
  };

  return (
    <PopoverContext.Provider value={popoverContext}>
      <PopoverContent
        {...bindContent}
        anchorPosition={anchorPosition}
        autoHide={autoHide}
        boundary={boundary}
        className={className}
        color={color}
        fallbackPlacements={fallbackPlacements}
        hasOutermostTheme={hasOutermostTheme}
        hideOverflow={hideOverflow}
        mountDuration={mountDuration}
        observeContentSize={observeContentSize}
        observeTargetSize={observeTargetSize}
        offset={offset}
        overflowPadding={overflowPadding}
        placement={placement}
        shadow={shadow}
        shouldAnimate={shouldAnimate}
        shouldUsePortal={shouldUsePortal}
        shouldUsePositioner
        unmountDuration={unmountDuration}
        zIndex={zIndex}
      >
        {content as React.ReactNode}
      </PopoverContent>
      {renderChildren()}
    </PopoverContext.Provider>
  );
}
